import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import { Stack } from '@mui/material'
import { ClientBriefDto } from '../../services/api/sharedTypes/Client'
import { stableSort, getComparator, Order, HeadCell } from '../../utils/tableFunctions'
import TableButtonDelete from '../table/TableButtonDelete'
import TableButtonOpen from '../table/TableButtonOpen'
import { convertClientTypeToTableString } from '../../utils/client-utils'
import { RouteConstants } from '../../navigation/navigation-types'
import EnhancedTableHead from '../table/EnhancedTableHead'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import PermContactCalendarRoundedIcon from '@mui/icons-material/PermContactCalendarRounded'
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded'
import CustomTableCell from '../common/CustomTableCell'
import { getNumberRoundedInFormattedString } from '../../utils/global-utils'

const headCells: readonly HeadCell[] = [
    {
        id: 'clientType',
        numeric: true,
        disablePadding: false,
        label: 'Typ klienta',
    },
    {
        id: 'name',
        numeric: false,
        disablePadding: false,
        label: 'Jméno / firma',
    },
    {
        id: 'companyRegistrationNumber',
        numeric: false,
        disablePadding: false,
        label: 'IČ',
    },
    {
        id: 'valueAddedTaxIdentificationNumber',
        numeric: false,
        disablePadding: false,
        label: 'DIČ',
    },
    {
        id: 'note',
        numeric: false,
        disablePadding: false,
        label: 'Poznámka',
    },
    {
        id: 'profit',
        numeric: true,
        disablePadding: false,
        label: 'Zisk',
        align: 'right',
    },
    {
        id: 'eventCount',
        numeric: true,
        disablePadding: false,
        label: 'Události',
        component: <EventRoundedIcon />,
        centered: true,
    },
    {
        id: 'contactCount',
        numeric: true,
        disablePadding: false,
        label: 'Kontakty',
        component: <PermContactCalendarRoundedIcon />,
        centered: true,
    },
    {
        id: 'notificationCount',
        numeric: true,
        disablePadding: false,
        label: 'Upozornění',
        component: <NotificationsNoneRoundedIcon />,
        centered: true,
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

interface ClientTableProps {
    clients: ClientBriefDto[]
    onRemoveClient: (id: string) => void
}

const ClientTable: React.FC<ClientTableProps> = ({ clients, onRemoveClient }: ClientTableProps) => {
    const [order, setOrder] = React.useState<Order>('asc')
    const [orderBy, setOrderBy] = React.useState<keyof ClientBriefDto>('name')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(60)

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property as keyof ClientBriefDto)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <Box style={{ width: '100%' }}>
            <TableContainer>
                <Table>
                    <EnhancedTableHead
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        rowCount={clients.length}
                    />
                    <TableBody>
                        {stableSort(
                            clients.map((row) => {
                                return {
                                    ...row,
                                    address: row.address
                                        ? row.address.city + ', ' + row.address.street + ' ' + row.address.zip
                                        : '',
                                    createdAt: new Date(row.createdAt).toLocaleDateString(),
                                    lastModifiedAt: new Date(row.lastModifiedAt).toLocaleDateString(),
                                    emailMessageSwitches: '',
                                    valueAddedTaxIdentificationNumber: row.valueAddedTaxIdentificationNumber || '',
                                    contacts: '',
                                    note: row.note || '',
                                }
                            }),
                            getComparator(order, orderBy)
                        )
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, index: number) => {
                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={index}
                                        style={{
                                            height: '42px',
                                        }}
                                    >
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer', minWidth: 110 }}
                                            route={RouteConstants.clients + '/' + row.id}
                                        >
                                            {convertClientTypeToTableString(row.clientType)}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer', width: '450px' }}
                                            route={RouteConstants.clients + '/' + row.id}
                                        >
                                            {row.name}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.clients + '/' + row.id}
                                        >
                                            {row.companyRegistrationNumber}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.clients + '/' + row.id}
                                        >
                                            {row.valueAddedTaxIdentificationNumber}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer', width: '320px' }}
                                            route={RouteConstants.clients + '/' + row.id}
                                        >
                                            {row.note}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            align='right'
                                            style={{ cursor: 'pointer', minWidth: 110 }}
                                            route={RouteConstants.clients + '/' + row.id}
                                        >
                                            {getNumberRoundedInFormattedString(row.profit)}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.clients + '/' + row.id}
                                        >
                                            {row.eventCount}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.clients + '/' + row.id}
                                        >
                                            {row.contactCount}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.clients + '/' + row.id}
                                        >
                                            {row.notificationCount}
                                        </CustomTableCell>
                                        <CustomTableCell>
                                            <Stack spacing={theme.spacing(2)} direction='row'>
                                                <TableButtonOpen route={RouteConstants.clients} id={row.id} />
                                                <TableButtonDelete
                                                    nameOfEntityInDialogMessage={row.name}
                                                    onClick={() => onRemoveClient(row.id)}
                                                />
                                            </Stack>
                                        </CustomTableCell>
                                    </TableRow>
                                )
                            })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[60, 100, 250]}
                component='div'
                count={clients.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Box>
    )
}

export default ClientTable
